import { useMemo, useState } from 'react';
import PageHeader from '@components/layout/page-header';
import { useTranslation } from 'react-i18next';
import { useGetStrandedSessionBo } from '@utils/api/back-office/stranded-sessions/use-get-stranded-sessions';
import { useGetAllStrandedSessionIds } from '@utils/api/back-office/stranded-sessions/use-get-all-stranded-session-ids';
import { useRetryStrandedSessions } from '@utils/api/back-office/stranded-sessions/use-post-retry';
import { formatDate, formatDuration } from '@utils/date';
import { useGetModels } from '@utils/api/models/use-get-models';
import Button from '@components/atoms/ui-kit/button.tsx';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';

import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { Badge } from '@components/atoms/ui-kit/badge.tsx';

type Row = {
  id: number;
  session_id: string;
  filename: string;
  customer_id: number;
  customer_name: string;
  status: string;
  duration: string; // formatted for display
  language: string;
  priority: number | null;
  is_deprioritized: boolean;
  date_session_start: string; // formatted
  last_update: string; // formatted
};

const STATUS_BADGE_COLOR: Record<
  string,
  'blue' | 'zinc' | 'yellow' | 'purple' | 'green' | 'red' | 'orange'
> = {
  uploading: 'blue',
  uploaded: 'blue',
  queued: 'zinc',
  processing: 'yellow',
  streaming: 'purple',
  finished: 'green',
  error: 'red',
  stranded: 'orange'
};

function StatusChip({ status }: { status: string }) {
  const color = STATUS_BADGE_COLOR[status.toLowerCase()] ?? 'zinc';
  return (
    <Badge color={color} className="capitalize">
      {status}
    </Badge>
  );
}

export default function StrandedSessions() {
  const { t } = useTranslation();
  const { data, isLoading, isFetching } = useGetStrandedSessionBo();
  const { data: allSessionData, isLoading: isLoadingAllIds } =
    useGetAllStrandedSessionIds();
  const { mutate: retrySessions } = useRetryStrandedSessions();
  const { data: supportedLanguages } = useGetModels();

  const [retryingItems, setRetryingItems] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const allSessionIds = allSessionData?.sessionIds ?? [];
  const totalSessions = allSessionData?.total ?? 0;

  const handleRetry = () => {
    if (!allSessionIds.length) return;
    setRetryingItems(true);
    // API takes a string array - retry ALL sessions
    retrySessions(allSessionIds, {
      onSettled: () => {
        setIsEditModalOpen(false);
        setRetryingItems(false);
      }
    });
  };

  const rows = useMemo<Row[]>(() => {
    if (!data) return [];
    return data.result.map(item => {
      const languageValue = item.language;
      const languageKey = languageValue?.split('-')[0]?.toLowerCase();
      const languageData = supportedLanguages?.result.find(
        lang => lang.key === languageKey
      );
      const fullName = languageData ? languageData.name : languageValue;

      return {
        id: item.id,
        session_id: item.session_id,
        customer_id: item.customer_id,
        customer_name: item.customer_name || '-',
        status: item.status,
        duration: formatDuration(Number(item.duration)),
        language: fullName || '',
        filename: item.filename,
        priority: item.priority ?? null,
        is_deprioritized: Boolean(item.is_deprioritized),
        date_session_start: formatDate(item.date_session_start || ''),
        last_update: formatDate(item.last_update || '')
      };
    });
  }, [data, supportedLanguages]);

  const columns = useMemo<DataGridColumn<Row>[]>(() => {
    return [
      {
        header: t('id'),
        field: 'id',
        maxWidth: 90,
        minWidth: 70,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'number',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('session-id'),
        field: 'session_id',
        minWidth: 240,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        enableRowCopyOnClick: true,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('filename'),
        field: 'filename',
        minWidth: 320,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        enableRowCopyOnClick: true,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('customer-id'),
        field: 'customer_id',
        maxWidth: 140,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'number',
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('customer-name'),
        field: 'customer_name',
        minWidth: 160,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('status'),
        field: 'status',
        maxWidth: 140,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        cell: ({ getValue }) => <StatusChip status={String(getValue() ?? '')} />
      },
      {
        header: t('priority'),
        field: 'priority',
        maxWidth: 150,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'number',
        cell: ({ getValue, row }) => {
          const value = getValue();
          const label =
            value === null || value === undefined ? '-' : String(value);
          if (!row.original.is_deprioritized) return label;
          return (
            <span className="inline-flex items-center gap-1">
              {label}
              <Badge color="orange" title={t('throttled-desc')}>
                {t('throttled')}
              </Badge>
            </span>
          );
        }
      },
      {
        header: t('duration'),
        field: 'duration',
        maxWidth: 120,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('language'),
        field: 'language',
        maxWidth: 140,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('date-session-start'),
        field: 'date_session_start',
        maxWidth: 200,
        enableSorting: false,
        enableFiltering: false, // keep as text unless your API expects date ops
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('last-update'),
        field: 'last_update',
        maxWidth: 200,
        enableSorting: false,
        enableFiltering: false, // keep as text
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '')
      }
    ];
  }, [t]);

  return (
    <div className="bg-app flex-1 min-h-0 flex flex-col px-4 py-2">
      <PageHeader
        title={t('stranded-sessions')}
        caption={t('stranded-session-desc')}
      >
        <Button
          onClick={() => setIsEditModalOpen(true)}
          label={
            totalSessions > 0
              ? `${t('retry-all-sessions')} (${totalSessions})`
              : t('retry-all-sessions')
          }
          disabled={isLoadingAllIds || totalSessions === 0}
        />
      </PageHeader>

      <div className="w-full p-2 md:p-0 flex-1 min-h-0 flex flex-col">
        <DataGridV2<Row>
          data={rows}
          columns={columns}
          enableRowSelection={false}
          enablePagination
          manualPagination
          enableSorting
          manualSorting
          enableFiltering
          manualFiltering
          totalItems={data?.total_results ?? rows.length}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          showColumnFilters={false}
          filterMode="popover"
          fill
          dense
          grid={false}
          striped
          loading={isLoading || isFetching || !data}
          getRowId={row => String(row.id)}
        />

        <ConfirmationModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onConfirm={handleRetry}
          size="small"
          title={t('retry-all-sessions')}
          caption={`${t('retry-all-sessions-confirmation')} (${totalSessions} ${t('sessions')})`}
          confirmLabel={retryingItems ? t('retrying') : t('retry')}
          cancelLabel={t('cancel')}
          confirmDisabled={retryingItems || !allSessionIds.length}
          destructive={false}
          autoFocusConfirm
        />
      </div>
    </div>
  );
}
